import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, blobErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { MobileCardList, MobileCard } from "@/components/MobileCards";
import { AccessBlocked } from "@/components/AccessBlocked";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Download, FileText, Plus, X, UserPlus } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";
import { fmtDate, istToday } from "@/lib/dateFormat";
import { useAuth } from "@/contexts/AuthContext";
import { MultiSelectDropdown, SelectedTags } from "@/components/ui/multi-select-dropdown";

const fmt = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function ProformaInvoicesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roles = user?.roles || [];
  const perms = user?.permissions || [];
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const STATES = getValues("indian_states");
  const CUSTOMER_TYPES = getValues("customer_types");
  const TRADE_FORMATS = getValues("channel_types");

  const [pis, setPis] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [tradeFormatFilter, setTradeFormatFilter] = useState([]);
  const [channelMultiFilter, setChannelMultiFilter] = useState([]);
  const [allChannels, setAllChannels] = useState([]);

  // Create PI dialog
  const [showCreate, setShowCreate] = useState(false);
  const [piMode, setPiMode] = useState("existing"); // "existing" | "prospect"
  const [allSKUs, setAllSKUs] = useState([]);
  const [channels, setChannels] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [custSearch, setCustSearch] = useState("");
  const [showCustResults, setShowCustResults] = useState(false);

  // Existing customer form
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Prospect form
  const [prospect, setProspect] = useState({ name: "", company_name: "", phone: "", email: "", state: "", gst_number: "" });

  // Shared PI fields
  const [piChannel, setPiChannel] = useState("");
  const [piLines, setPiLines] = useState([{ sku_id: "", sku_name: "", quantity: 1, selling_price: 0, discount_percent: 0 }]);
  const [piTerms, setPiTerms] = useState("As agreed");
  const [piNotes, setPiNotes] = useState("");
  const [generating, setGenerating] = useState(false);

  // Convert dialog
  const [showConvert, setShowConvert] = useState(false);
  const [convertPI, setConvertPI] = useState(null);
  const [convertType, setConvertType] = useState("General Trade");
  const [converting, setConverting] = useState(false);

  const loadPIs = async (pg = page) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      if (customerSearch) params.customer = customerSearch;
      const { data } = await api.get("/proforma-invoices", { params });
      setPis(data.pis || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(data.page || 1);
      setBlocked(null);
    } catch (err) {
      // FX3 A4/C11 — a zero-permission user must see an access state, not every PI
      if ([401, 403].includes(err.response?.status)) { setPis([]); setBlocked(err.response?.data?.detail || "No access"); }
      else console.error("API get /proforma-invoices error:", err);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadPIs(1); }, [customerSearch]);
  useEffect(() => { api.get("/channels").then(r => setAllChannels(r.data || [])).catch(err => console.error("API get /channels error:", err)); }, []);

  const filteredChannelsPI = tradeFormatFilter.length > 0 ? allChannels.filter(c => tradeFormatFilter.includes(c.type)) : allChannels;
  const channelOptionsPI = filteredChannelsPI.filter(c => c.status === "active").map(c => ({ value: c._id, label: c.name, group: c.type || "Other" }));
  const chNameMap = Object.fromEntries(allChannels.map(c => [c._id, c.name]));

  // Client-side filter PIs by channel
  const filteredPIs = pis.filter(p => {
    if (channelMultiFilter.length > 0 && !channelMultiFilter.includes(p.channel_id)) return false;
    return true;
  });

  const openCreate = () => {
    setPiMode("existing");
    setSelectedCustomer(null);
    setProspect({ name: "", company_name: "", phone: "", email: "", state: "", gst_number: "" });
    setPiChannel("");
    setPiLines([{ sku_id: "", sku_name: "", quantity: 1, selling_price: 0, discount_percent: 0 }]);
    setPiTerms("As agreed"); setPiNotes("");
    if (allSKUs.length === 0) api.get("/catalogue/skus").then(r => setAllSKUs(r.data || [])).catch(err => console.error("API get /catalogue/skus error:", err));
    if (channels.length === 0) api.get("/admin/channels").then(r => setChannels(r.data || [])).catch(err => console.error("API get /admin/channels error:", err));
    setShowCreate(true);
  };

  const searchCustomers = async (q) => {
    setCustSearch(q);
    if (q.length < 2) { setCustomers([]); return; }
    try {
      const { data } = await api.get("/customers", { params: { search: q } });
      // PASS 2 ZIP 1 FIX-UP (Fix 3b): the picker never offers an Inactive customer
      setCustomers((data || []).filter(c => (c.status || "") !== "Inactive"));
      setShowCustResults(true);
    } catch (err) { console.error("API get /customers error:", err); }
  };

  const updatePILine = (idx, field, value) => {
    const d = [...piLines];
    d[idx] = { ...d[idx], [field]: value };
    if (field === "sku_id") {
      const sku = allSKUs.find(s => s.sku_id === value);
      if (sku) {
        d[idx].sku_name = sku.sku_name;
        let price = sku.mrp || 0;
        if (piChannel) {
          const ca = (sku.channel_access || []).find(c => c.channel_id === piChannel && c.enabled);
          if (ca && ca.selling_price > 0) price = ca.selling_price;
        }
        d[idx].selling_price = price;
      }
    }
    setPiLines(d);
  };

  const handleGenerate = async () => {
    const activeLines = piLines.filter(l => l.sku_id && l.quantity > 0);
    if (activeLines.length === 0) { toast.error("Add at least one SKU with quantity"); return; }

    setGenerating(true);
    try {
      let response;
      if (piMode === "prospect") {
        if (!prospect.name) { toast.error("Prospect name is required"); setGenerating(false); return; }
        if (!prospect.state) { toast.error("State is required for GST calculation"); setGenerating(false); return; }
        const ch = channels.find(c => c._id === piChannel);
        response = await api.post("/proforma-invoices/prospect", {
          prospect_name: prospect.name,
          company_name: prospect.company_name,
          phone: prospect.phone,
          email: prospect.email,
          state: prospect.state,
          gst_number: prospect.gst_number,
          channel_id: piChannel, channel_name: ch?.name || "",
          line_items: activeLines, payment_terms: piTerms, notes: piNotes,
        }, { responseType: "blob" });
      } else {
        if (!selectedCustomer) { toast.error("Select a customer"); setGenerating(false); return; }
        response = await api.post(`/customers/${selectedCustomer._id}/proforma-invoice`, {
          customer_id: selectedCustomer._id,
          line_items: activeLines, payment_terms: piTerms, notes: piNotes,
        }, { responseType: "blob" });
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `PI-${piMode === "prospect" ? prospect.name : selectedCustomer?.name}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Proforma Invoice generated");
      setShowCreate(false);
      loadPIs(1);
    } catch (err) { toast.error(await blobErrorMessage(err, "Failed to generate PI")); }
    finally { setGenerating(false); }
  };

  const handleConvert = async () => {
    if (!convertPI) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/proforma-invoices/${convertPI._id}/convert-to-customer`, { type: convertType });
      toast.success(`Customer "${data.customer_name}" created from prospect`);
      setShowConvert(false);
      loadPIs(page);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setConverting(false); }
  };

  const downloadPI = async (pi) => {
    try {
      if (pi.order_id) {
        const response = await api.get(`/orders/${pi.order_id}/proforma-invoice`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        const a = document.createElement("a"); a.href = url; a.download = `${pi.pi_number.replace(/\//g, "-")}.pdf`; a.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.info("Customer/prospect PIs are downloaded at generation time.");
      }
    } catch (err) { toast.error(await blobErrorMessage(err, "Failed to download PI")); }
  };

  return (
    <div data-testid="pi-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Proforma Invoices</h1>
          <p className="text-sm text-zinc-500 mt-1">{total} PIs generated</p>
        </div>
        <div className="flex gap-2">
          {(roles.includes("Admin") || roles.includes("admin") || perms.includes("pi.export")) && <Button variant="outline" className="rounded-sm h-8 text-xs" data-testid="export-pi-btn" onClick={() => {
            const params = new URLSearchParams();
            if (customerSearch) params.set("customer", customerSearch);
            api.get(`/proforma-invoices/export?${params.toString()}`, { responseType: "blob" }).then(r => {
              const url = window.URL.createObjectURL(new Blob([r.data]));
              const a = document.createElement("a"); a.href = url; a.download = `ProformaInvoices_${istToday()}.xlsx`; a.click();
              toast.success("PIs exported");
            }).catch(async err => toast.error(err.response?.status === 400 ? "No data to export" : await blobErrorMessage(err, "Export failed")));
          }}><Download size={14} /> Export Excel</Button>}
          <Button onClick={openCreate} className="rounded-sm h-8 text-xs bg-blue-600 hover:bg-blue-700" data-testid="create-pi-btn">
            <Plus size={14} /> Create PI
          </Button>
        </div>
      </div>

      <div className="flex gap-3 mb-3 flex-wrap items-end">
        <div className="relative w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search customer..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} className="pl-9 h-8 text-xs rounded-sm" data-testid="pi-search" />
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Trade Format</span>
          <MultiSelectDropdown label="Trade Format" options={TRADE_FORMATS} selected={tradeFormatFilter} onChange={v => { setTradeFormatFilter(v); setChannelMultiFilter([]); }} />
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Sales Channel</span>
          <MultiSelectDropdown label="Sales Channel" options={channelOptionsPI} selected={channelMultiFilter} onChange={setChannelMultiFilter} groupBy="group" />
        </div>
        {(tradeFormatFilter.length > 0 || channelMultiFilter.length > 0) && <Button variant="ghost" className="h-8 text-[10px] text-zinc-500" onClick={() => { setTradeFormatFilter([]); setChannelMultiFilter([]); }}>Clear Filters</Button>}
      </div>
      {(tradeFormatFilter.length > 0 || channelMultiFilter.length > 0) && (
        <div className="flex flex-wrap gap-1 mb-2">
          <SelectedTags selected={tradeFormatFilter} options={TRADE_FORMATS} onRemove={v => setTradeFormatFilter(tradeFormatFilter.filter(x => x !== v))} />
          <SelectedTags selected={channelMultiFilter} options={channelOptionsPI} onRemove={v => setChannelMultiFilter(channelMultiFilter.filter(x => x !== v))} />
        </div>
      )}

      {blocked && <AccessBlocked detail={blocked} testid="pi-access-blocked" />}

      {!blocked && <>
      {/* FX3 C12 — mobile card view: the Amount column is always visible */}
      <MobileCardList testid="pi-cards">
        {loading ? <p className="text-center py-8 text-zinc-400 text-xs">Loading...</p> :
         filteredPIs.length === 0 ? <p className="text-center py-8 text-zinc-400 text-xs">No proforma invoices found</p> :
         filteredPIs.map(pi => (
          <MobileCard
            key={pi._id}
            testid={`pi-card-${pi._id}`}
            title={pi.pi_number}
            subtitle={pi.customer_name}
            amount={`₹${fmt(pi.grand_total)}`}
            badge={<span className={`inline-block mt-1 px-1.5 py-0.5 text-[8px] font-bold rounded-sm ${pi.pi_type === "prospect" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{pi.pi_type === "prospect" ? "PROSPECT" : "CUSTOMER"}</span>}
            rows={[
              { label: "Date", value: pi.pi_date || fmtDate(pi.created_at) },
              { label: "Linked order", value: pi.order_number || "Quotation" },
              { label: "Generated by", value: pi.generated_by_name },
            ]}
            actions={<>
              {pi.order_id && <button onClick={() => downloadPI(pi)} className="p-1 hover:bg-blue-50 rounded-sm" title="Download PI" data-testid={`pi-card-download-${pi._id}`}><Download size={14} className="text-blue-500" /></button>}
              {pi.pi_type === "prospect" && (
                <button onClick={() => { setConvertPI(pi); setConvertType("General Trade"); setShowConvert(true); }} className="p-1 hover:bg-green-50 rounded-sm" title="Convert to Customer" data-testid={`pi-card-convert-${pi._id}`}>
                  <UserPlus size={14} className="text-green-600" />
                </button>
              )}
            </>}
          />
        ))}
      </MobileCardList>

      <div className="hidden md:block border border-zinc-200 overflow-x-auto" data-testid="pi-table">
        <table className="data-table w-full">
          <thead><tr><th>PI Number</th><th>Type</th><th>Date</th><th>Customer</th><th>Linked Order</th><th className="num">Amount</th><th>Generated By</th><th className="w-24">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            filteredPIs.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">No proforma invoices found</td></tr> :
            filteredPIs.map(pi => (
              <tr key={pi._id} data-testid={`pi-row-${pi._id}`}>
                <td className="font-mono text-xs font-semibold">{pi.pi_number}</td>
                <td>
                  {pi.pi_type === "prospect" ? (
                    <span className="px-1.5 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-700 rounded-sm">PROSPECT</span>
                  ) : (
                    <span className="px-1.5 py-0.5 text-[8px] font-bold bg-green-100 text-green-700 rounded-sm">CUSTOMER</span>
                  )}
                </td>
                <td className="text-xs text-zinc-500">{pi.pi_date || fmtDate(pi.created_at)}</td>
                <td className="text-xs font-medium">{pi.customer_name}</td>
                <td className="text-xs">{pi.order_number || <span className="text-zinc-400 italic">Quotation</span>}</td>
                <td className="num font-mono text-xs font-semibold">₹{fmt(pi.grand_total)}</td>
                <td className="text-xs text-zinc-500">{pi.generated_by_name}</td>
                <td>
                  <div className="flex gap-1">
                    {pi.order_id && <button onClick={() => downloadPI(pi)} className="p-1 hover:bg-blue-50 rounded-sm" title="Download PI"><Download size={13} className="text-blue-500" /></button>}
                    {pi.pi_type === "prospect" && (
                      <button onClick={() => { setConvertPI(pi); setConvertType("General Trade"); setShowConvert(true); }} className="p-1 hover:bg-green-50 rounded-sm" title="Convert to Customer" data-testid={`convert-btn-${pi._id}`}>
                        <UserPlus size={13} className="text-green-600" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" className="rounded-sm h-7 text-xs" disabled={page <= 1} onClick={() => loadPIs(page - 1)}>Prev</Button>
          <span className="text-xs text-zinc-500">Page {page} of {pages}</span>
          <Button variant="outline" className="rounded-sm h-7 text-xs" disabled={page >= pages} onClick={() => loadPIs(page + 1)}>Next</Button>
        </div>
      )}

      {/* Create PI Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-pi-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create Proforma Invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Mode toggle */}
            <div className="flex gap-2">
              <Button variant={piMode === "existing" ? "default" : "outline"} className="rounded-sm text-xs h-8 flex-1" onClick={() => setPiMode("existing")} data-testid="mode-existing">Existing Customer</Button>
              <Button variant={piMode === "prospect" ? "default" : "outline"} className="rounded-sm text-xs h-8 flex-1" onClick={() => setPiMode("prospect")} data-testid="mode-prospect">New Prospect</Button>
            </div>

            {/* Existing Customer search */}
            {piMode === "existing" && (
              <div className="relative">
                <Label className="text-xs">Search Customer</Label>
                {selectedCustomer ? (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-xs bg-blue-50 border border-blue-200 px-2 py-1 rounded-sm flex-1">{selectedCustomer.name}{selectedCustomer.company_name ? ` (${selectedCustomer.company_name})` : ""}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedCustomer(null)}><X size={12} /></Button>
                  </div>
                ) : (
                  <>
                    <Input value={custSearch} onChange={e => searchCustomers(e.target.value)} onFocus={() => { if (customers.length) setShowCustResults(true); }}
                      className="mt-1 rounded-sm text-xs" placeholder="Type customer name..." data-testid="pi-cust-search" />
                    {showCustResults && customers.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-zinc-200 rounded-sm shadow-lg max-h-36 overflow-y-auto">
                        {customers.map(c => (
                          <button key={c._id} onClick={() => { setSelectedCustomer(c); setCustSearch(""); setShowCustResults(false); }}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b border-zinc-50">{c.name}{c.company_name ? ` (${c.company_name})` : ""} <span className="text-zinc-400">{c.state}</span></button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Prospect form */}
            {piMode === "prospect" && (
              <div className="border border-amber-200 bg-amber-50 p-3 rounded-sm space-y-2">
                <p className="text-[10px] font-semibold text-amber-800">Prospect Details</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[10px]">Prospect Name *</Label><Input value={prospect.name} onChange={e => setProspect({...prospect, name: e.target.value})} className="mt-0.5 rounded-sm text-xs h-7" data-testid="prospect-name" /></div>
                  <div><Label className="text-[10px]">Company Name</Label><Input value={prospect.company_name} onChange={e => setProspect({...prospect, company_name: e.target.value})} className="mt-0.5 rounded-sm text-xs h-7" /></div>
                  <div><Label className="text-[10px]">Phone</Label><Input value={prospect.phone} onChange={e => setProspect({...prospect, phone: e.target.value})} className="mt-0.5 rounded-sm text-xs h-7" /></div>
                  <div><Label className="text-[10px]">Email</Label><Input value={prospect.email} onChange={e => setProspect({...prospect, email: e.target.value})} className="mt-0.5 rounded-sm text-xs h-7" /></div>
                  <div><Label className="text-[10px]">State *</Label>
                    <Select value={prospect.state || "none"} onValueChange={v => setProspect({...prospect, state: v === "none" ? "" : v})}>
                      <SelectTrigger className="mt-0.5 rounded-sm text-xs h-7" data-testid="prospect-state"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[10px]">GST Number <span className="text-zinc-400">(optional)</span></Label><Input value={prospect.gst_number} onChange={e => setProspect({...prospect, gst_number: e.target.value.toUpperCase().slice(0, 15)})} className="mt-0.5 rounded-sm text-xs h-7" placeholder="If blank: 'Applied for'" maxLength={15} /></div>
                </div>
              </div>
            )}

            {/* Channel */}
            <div>
              <Label className="text-xs">Sales Channel</Label>
              <Select value={piChannel || "none"} onValueChange={v => setPiChannel(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1 rounded-sm text-xs" data-testid="pi-channel"><SelectValue placeholder="Select channel for pricing" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-- No channel (use MRP) --</SelectItem>{channels.map(ch => <SelectItem key={ch._id} value={ch._id}>{ch.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* SKU selection */}
            <div>
              <Label className="text-xs font-semibold">SKU Selection</Label>
              <div className="grid grid-cols-12 gap-2 mt-2 mb-1">
                <div className="col-span-5"><span className="text-[9px] font-semibold uppercase text-zinc-400">SKU</span></div>
                <div className="col-span-2"><span className="text-[9px] font-semibold uppercase text-zinc-400">Qty</span></div>
                <div className="col-span-2"><span className="text-[9px] font-semibold uppercase text-zinc-400">Unit Price</span></div>
                <div className="col-span-2"><span className="text-[9px] font-semibold uppercase text-zinc-400">Disc %</span></div>
                <div className="col-span-1"></div>
              </div>
              {piLines.map((pl, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mt-1 items-end" data-testid={`pi-line-${idx}`}>
                  <div className="col-span-5">
                    <Select value={pl.sku_id || "none"} onValueChange={v => updatePILine(idx, "sku_id", v === "none" ? "" : v)}>
                      <SelectTrigger className="h-7 text-xs rounded-sm"><SelectValue placeholder="Select SKU" /></SelectTrigger>
                      <SelectContent>{allSKUs.filter(s => s.status !== "Draft").map(s => <SelectItem key={s.sku_id} value={s.sku_id}>{s.sku_name} ({s.sku_id})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Input type="number" min={1} value={pl.quantity} onChange={e => updatePILine(idx, "quantity", parseInt(e.target.value) || 0)} className="h-7 text-xs rounded-sm text-right" /></div>
                  <div className="col-span-2"><Input type="number" step="0.01" value={pl.selling_price} onChange={e => updatePILine(idx, "selling_price", parseFloat(e.target.value) || 0)} className="h-7 text-xs rounded-sm text-right" /></div>
                  <div className="col-span-2"><Input type="number" step="0.1" value={pl.discount_percent} onChange={e => updatePILine(idx, "discount_percent", parseFloat(e.target.value) || 0)} className="h-7 text-xs rounded-sm text-right" /></div>
                  <div className="col-span-1">{piLines.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => setPiLines(piLines.filter((_, i) => i !== idx))}><X size={12} /></Button>}</div>
                </div>
              ))}
              <Button variant="ghost" className="h-6 text-[10px] mt-1" onClick={() => setPiLines([...piLines, { sku_id: "", sku_name: "", quantity: 1, selling_price: 0, discount_percent: 0 }])}>+ Add SKU</Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Payment Terms</Label><Input value={piTerms} onChange={e => setPiTerms(e.target.value)} className="mt-1 rounded-sm text-xs" /></div>
              <div><Label className="text-xs">Notes</Label><Input value={piNotes} onChange={e => setPiNotes(e.target.value)} className="mt-1 rounded-sm text-xs" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating} className="rounded-sm text-xs bg-blue-600 hover:bg-blue-700" data-testid="generate-pi-submit">
              <FileText size={12} /> {generating ? "Generating..." : "Generate PI"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Customer Dialog */}
      <Dialog open={showConvert} onOpenChange={setShowConvert}>
        <DialogContent className="max-w-sm" data-testid="convert-dialog">
          <DialogHeader><DialogTitle className="font-heading">Convert Prospect to Customer</DialogTitle></DialogHeader>
          {convertPI && (
            <div className="space-y-3">
              <div className="bg-zinc-50 p-3 rounded-sm text-xs">
                <p><span className="text-zinc-500">Name:</span> <span className="font-semibold">{convertPI.prospect_details?.name || convertPI.customer_name}</span></p>
                {convertPI.prospect_details?.company_name && <p><span className="text-zinc-500">Company:</span> {convertPI.prospect_details.company_name}</p>}
                {convertPI.prospect_details?.state && <p><span className="text-zinc-500">State:</span> {convertPI.prospect_details.state}</p>}
                {convertPI.prospect_details?.gst_number && <p><span className="text-zinc-500">GST:</span> {convertPI.prospect_details.gst_number}</p>}
                <p className="mt-1"><span className="text-zinc-500">PI:</span> {convertPI.pi_number} — ₹{fmt(convertPI.grand_total)}</p>
              </div>
              <div>
                <Label className="text-xs">Customer Type</Label>
                <Select value={convertType} onValueChange={v => setConvertType(v)}>
                  <SelectTrigger className="mt-1 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{CUSTOMER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvert(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleConvert} disabled={converting} className="rounded-sm text-xs" data-testid="convert-submit">
              <UserPlus size={12} /> {converting ? "Converting..." : "Create Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
